/* Green Gorilla Fruits — referral JWT handshake on cart entry
 * --------------------------------------------------------------
 * Referees that landed via ?ref=REF-XXXXXXXX and signed up through
 * Shopify Customer Accounts have a Shopify session but no ec-reffer
 * JWT (they never visit /pages/refer). Without a JWT, referral-redeem.js
 * skips the redeem call and the referee's $10 coupon is never issued.
 *
 * This module bridges that gap: on /cart entry, if a Shopify customer
 * is logged in AND a stored referral code exists AND no JWT is present
 * yet, it redirects once to the ec-reffer OAuth login.
 * The callback drops a JWT into localStorage (#session=<jwt> →
 * gg_customer_session), at which point referral-redeem.js can fire.
 *
 * Retry policy: a sessionStorage flag suppresses repeat redirects
 * within the same tab. Closing and re-opening the tab allows a fresh
 * attempt so transient failures (network, OAuth provider blip) self-heal.
 *
 * Public API: window.GG_ReferralHandshake.run()
 */
(function () {
  'use strict';

  const HANDSHAKE_FLAG_KEY = 'gg_referral_jwt_handshake';
  // Must stay in sync with assets/customer-account-token.js STORAGE_KEY and
  // the inline bootstrap in layout/theme.liquid. Update all three together.
  const SESSION_STORAGE_KEY = 'gg_customer_session';
  // /checkout is hosted on a separate Shopify domain where this theme's
  // layout/theme.liquid is not evaluated, so /cart is the only practical
  // entry point for the handshake. Dynamic checkout buttons that skip the
  // cart are out of scope and tracked separately.
  const CART_PATHS = ['/cart'];
  const DEBUG_KEY = 'gg_debug';
  const LOG_PREFIX = '[gg-referral][handshake]';

  function debugEnabled() {
    try {
      return localStorage.getItem(DEBUG_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  function log() {
    if (!debugEnabled()) return;
    const args = Array.prototype.slice.call(arguments);
    args.unshift(LOG_PREFIX);
    try {
      // eslint-disable-next-line no-console
      console.log.apply(console, args);
    } catch (e) {
      /* ignore */
    }
  }

  function getConfig() {
    return window.GG_REFERRAL_CONFIG && typeof window.GG_REFERRAL_CONFIG === 'object'
      ? window.GG_REFERRAL_CONFIG
      : {};
  }

  function isCartPath() {
    const path = window.location.pathname;
    return CART_PATHS.some((prefix) => path === prefix || path.indexOf(prefix + '/') === 0);
  }

  function hasShopifyCustomer() {
    const id = window.GG_SHOPIFY_CUSTOMER_ID;
    if (id === null || id === undefined || id === '') return false;
    // Shopify renders {{ customer.id }} as a bare integer (e.g. 9617282367680)
    // for logged-in customers and `null` for guests. Treat any non-empty,
    // non-falsy primitive as logged in.
    return true;
  }

  function getReferralCode() {
    if (window.GG_Referral && typeof window.GG_Referral.getCode === 'function') {
      return window.GG_Referral.getCode();
    }
    return null;
  }

  function hasStoredJwt() {
    try {
      const jwt = localStorage.getItem(SESSION_STORAGE_KEY);
      return !!jwt;
    } catch (e) {
      // localStorage unavailable — treat as if a JWT exists so we never
      // trigger an OAuth loop in private mode where we cannot persist
      // the resulting token.
      return true;
    }
  }

  function alreadyAttempted() {
    try {
      return sessionStorage.getItem(HANDSHAKE_FLAG_KEY) === '1';
    } catch (e) {
      // sessionStorage unavailable — refuse to redirect to avoid loops.
      return true;
    }
  }

  function markAttempted() {
    try {
      sessionStorage.setItem(HANDSHAKE_FLAG_KEY, '1');
    } catch (e) {
      /* ignore — alreadyAttempted() returns true on read failure */
    }
  }

  function buildSafeReturn() {
    const path = window.location.pathname || '/cart';
    const search = window.location.search || '';
    const combined = path + search;
    // Require a same-origin absolute path; reject protocol-relative URLs
    // so an attacker cannot smuggle an off-site return_to via location.
    if (!combined.startsWith('/') || combined.startsWith('//')) return '/cart';
    return combined;
  }

  function buildLoginUrl(base, returnTo) {
    const normalized = (base || '/apps/referral').replace(/\/+$/, '');
    return normalized + '/auth/customer/login?return_to=' + encodeURIComponent(returnTo);
  }

  function run() {
    if (!isCartPath()) {
      log('run: not on cart path — skip');
      return;
    }
    if (!hasShopifyCustomer()) {
      log('run: no Shopify customer — skip');
      return;
    }
    if (hasStoredJwt()) {
      log('run: ec-reffer JWT already present — skip');
      return;
    }
    const code = getReferralCode();
    if (!code) {
      log('run: no stored referral code — skip');
      return;
    }
    if (alreadyAttempted()) {
      log('run: handshake already attempted this tab — skip');
      return;
    }

    // Mark before redirect so a navigation rejected by an extension /
    // network failure does not loop within the same tab. Closing and
    // re-opening the tab clears sessionStorage and allows a fresh attempt.
    markAttempted();
    const cfg = getConfig();
    const returnTo = buildSafeReturn();
    const url = buildLoginUrl(cfg.appProxyBase, returnTo);
    log('run: redirecting to ec-reffer OAuth login', { url: url, returnTo: returnTo });
    window.location.replace(url);
  }

  window.GG_ReferralHandshake = { run };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
